import type { VariantProps } from "class-variance-authority";
import { useEffect, useRef, useState, type ButtonHTMLAttributes, type ReactNode } from "react";
import { cn } from "../cn";
import { buttonVariants as button } from "./button-variants";
import { ButtonIcon } from "./button-icon";
import type { IconName } from "./icon";
import { Spinner } from "./spinner";

const COPIED_MS = 1200;
/** Icons that read as a destination, so they sit after the label. */
const TRAILING_ICONS: readonly IconName[] = ["arrow", "external"];

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof button> {
  children?: ReactNode;
  /**
   * Working. The label and a spinner share one grid cell, so the width holds
   * while one leaves and the other arrives. The button is disabled and busy.
   * A `refresh` icon is the spinner.
   */
  loading?: boolean;
  /** What the button does, drawn beside the label and moving with the button's state. */
  icon?: IconName;
  /** Leading unless the icon is a destination (`arrow`, `external`). */
  iconPosition?: "start" | "end";
  /** The action succeeded: the icon becomes a tick for as long as this holds. A `copy` icon does this on its own for a beat. */
  done?: boolean;
}

/** A button. Says exactly what happens when pressed. */
export function Button({
  className,
  variant,
  size,
  weight,
  loading,
  icon,
  iconPosition,
  done,
  children,
  disabled,
  onClick,
  ...props
}: ButtonProps) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);

  const ticked = done ?? (icon === "copy" && copied);
  const press: ButtonHTMLAttributes<HTMLButtonElement>["onClick"] =
    icon === "copy" && done === undefined
      ? (event) => {
          onClick?.(event);
          setCopied(true);
          clearTimeout(timer.current);
          timer.current = setTimeout(() => setCopied(false), COPIED_MS);
        }
      : onClick;

  const mark = icon ? <ButtonIcon name={icon} done={ticked} /> : null;
  const end =
    icon !== undefined &&
    (iconPosition ?? (TRAILING_ICONS.includes(icon) ? "end" : "start")) === "end";
  const content = icon ? (
    <>
      {end ? null : mark}
      {children}
      {end ? mark : null}
    </>
  ) : (
    children
  );

  if (loading === undefined) {
    return (
      <button
        className={cn(button({ variant, size, weight }), className)}
        disabled={disabled}
        onClick={press}
        {...props}
      >
        {content}
      </button>
    );
  }
  return (
    <button
      className={cn(button({ variant, size, weight }), "inline-grid", className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      onClick={press}
      {...props}
    >
      <span
        data-on={loading ? undefined : ""}
        className="col-start-1 row-start-1 inline-flex items-center justify-center gap-2 transition-[opacity,transform] duration-(--motion-fast) ease-(--ease-in) not-data-on:translate-y-[calc(var(--motion-nudge)*-1)] not-data-on:opacity-0 data-on:duration-(--motion-base) data-on:ease-(--ease-out)"
      >
        {content}
      </span>
      <span
        aria-hidden="true"
        data-on={loading ? "" : undefined}
        className="col-start-1 row-start-1 grid place-items-center transition-[opacity,transform] duration-(--motion-fast) ease-(--ease-in) not-data-on:translate-y-[var(--motion-nudge)] not-data-on:opacity-0 data-on:duration-(--motion-base) data-on:ease-(--ease-out)"
      >
        {icon === "refresh" ? <ButtonIcon name="refresh" loading /> : <Spinner />}
      </span>
    </button>
  );
}
